// fw DesktopShell — the frame for Office.
//
// A 184px sidebar (surface) with grouped links; the active link is surface-2
// at weight 600 and carries aria-current. Main area padding 20px 24px.
//
// THE BRAND IS A HEADER, NOT A ROW IN THE LIST. It sits outside
// .fw-desktop__scroll, which is the only thing in the sidebar that scrolls,
// and it renders the uploaded logo (`settings.brand_logo`, passed in as
// `logo`) when there is one and the `brand` wordmark when there is not. Both
// halves of that are fixes to defects the owner reported from a screenshot on
// 2026-09-08 — see DesktopShell.test.js for the measurements.
//
// GROUPS CAN COLLAPSE, AND ONLY WHEN THE CALLER ASKS. A group carrying a
// boolean `open` renders its heading as a disclosure button and draws its rows
// only when open; a group without one renders exactly as it always did. The
// caller owns the state — which groups start open is a question about a ROLE
// and this component knows nothing about roles (apps/shared/navigation.js
// `defaultOpenGroupsFor`, apps/shell/navGroups.js for what a person chose).
// Why it exists at all: an admin's sidebar measured 26 equally weighted rows
// and 1495px of content in an 844px column at 1440x900, so ten of their rows
// could not be seen without scrolling a sticky element.
//
// THE ONE RESPONSIVE RULE IN THE SYSTEM: on a phone the sidebar collapses to
// a top bar with a menu button, and the groups open as a drawer.
// usePhoneLayout decides (matchMedia, innerWidth fallback); the stylesheet
// mirrors the same query. No other layout in fw changes shape by itself.
//
// "On a phone" is not "below --fw-phone-max". A landscape iPhone 12 is
// 844x390, so width alone put 280px of sidebar on a screen 390px tall — which
// is what the owner was holding when they asked why the mobile app was showing
// as desktop. The query carries a height-plus-coarse-pointer clause for that;
// see usePhoneLayout.js for the measurements and the four devices it sorts.

import React, { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon.js';
import { usePhoneLayout } from '../usePhoneLayout.js';
import '../tokens.css';
import '../base.css';
import './DesktopShell.css';

/** A count worth drawing: not undefined, not null, not 0, not ''. */
function hasBadge(n) {
  return n !== undefined && n !== null && n !== 0 && n !== '';
}

function NavLink({ item, activeKey, onNavigate }) {
  const active = item.key === activeKey;
  return (
    <button
      type="button"
      className={['fw-desktop__link', active ? 'fw-desktop__link--active' : ''].filter(Boolean).join(' ')}
      aria-current={active ? 'page' : undefined}
      data-key={item.key}
      onClick={() => onNavigate(item.key)}
    >
      {item.icon ? <span className="fw-desktop__link-icon"><Icon name={item.icon} /></span> : null}
      <span className="fw-desktop__link-label">{item.label}</span>
      {hasBadge(item.badge) ? <span className="fw-desktop__badge fw-num">{item.badge}</span> : null}
    </button>
  );
}

/**
 * The groups, and — when the caller asks for it — their disclosure.
 *
 * A group is COLLAPSIBLE only when it carries a boolean `open`. Without one it
 * renders exactly as it always did: a plain heading and every row under it.
 * That is what keeps this additive — a caller that knows nothing about
 * collapsing gets byte-identical markup, and `apps/shell/AppShell.js` is the
 * only caller that does.
 *
 * A CLOSED group's rows are not in the DOM at all, rather than hidden with an
 * attribute. That is not a style preference: this component shipped a drawer
 * that stayed mounted under `hidden`, the sidebar's own `display: flex` beat
 * the attribute, and every phone screen was painted over by a menu with its
 * close button underneath it — in production, on 2026-09-03. Not rendering is
 * the only version of "not showing" that cannot lose that argument.
 */
function NavGroups({ groups, activeKey, onNavigate, onToggleGroup }) {
  return (
    <>
      {groups.map((group, gi) => {
        const collapsible = typeof group.open === 'boolean' && !!group.label;
        const open = !collapsible || group.open;
        const panelId = `fw-navgroup-${gi}`;
        return (
          <div
            className={['fw-desktop__group', collapsible ? 'fw-desktop__group--collapsible' : '', collapsible && !open ? 'is-closed' : ''].filter(Boolean).join(' ')}
            key={group.label || gi}
          >
            {/*
              `locked` is the group holding the screen you are on. It is open
              and it cannot be closed — you would be standing in a collapsed
              group with no way to see out of it. Before this it was a plain
              button that persisted a preference and changed nothing on
              screen, which from the user's side is a control that does
              nothing when pressed (found by review on #652).

              `aria-disabled`, not `disabled`: the heading is still worth
              reading and still worth reaching with a keyboard, and a screen
              reader should say it is not actionable rather than skip it. The
              click is a no-op at the source too, so no preference is written
              for a group whose state is forced.
            */}
            {collapsible ? (
              <button
                type="button"
                className="fw-desktop__group-toggle"
                aria-expanded={open ? 'true' : 'false'}
                aria-controls={panelId}
                aria-disabled={group.locked ? 'true' : undefined}
                data-locked={group.locked ? 'true' : undefined}
                title={group.locked ? 'This is the section you are in' : undefined}
                data-group={group.label}
                onClick={() => { if (!group.locked && onToggleGroup) onToggleGroup(group.label); }}
              >
                <span className="fw-desktop__group-label">{group.label}</span>
                {hasBadge(group.badge) ? <span className="fw-desktop__badge fw-num">{group.badge}</span> : null}
                <span className="fw-desktop__group-chevron"><Icon name={open ? 'chevronUp' : 'chevronDown'} /></span>
              </button>
            ) : group.label ? (
              <div className="fw-desktop__group-label">{group.label}</div>
            ) : null}
            {collapsible ? (
              <div id={panelId}>
                {open ? (group.items || []).map((item) => (
                  <NavLink key={item.key} item={item} activeKey={activeKey} onNavigate={onNavigate} />
                )) : null}
              </div>
            ) : (group.items || []).map((item) => (
              <NavLink key={item.key} item={item} activeKey={activeKey} onNavigate={onNavigate} />
            ))}
          </div>
        );
      })}
    </>
  );
}

export function DesktopShell({
  brand, logo = '', logoAlt = '',
  groups = [], activeKey, onNavigate, onToggleGroup, user, onLogout, topRight, children, className = '', ...rest
}) {
  const phone = usePhoneLayout();
  const [menuOpen, setMenuOpen] = useState(false);
  // An uploaded logo can be a data: URL, an /uploads path off a box that has
  // been reinstalled, or a URL an admin typed. When one does not load the app
  // must still be named, so a failed image falls back to the wordmark — and a
  // NEW value gets a fresh chance, or one bad paste would wedge the fallback
  // on for the life of the tab.
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => { setLogoFailed(false); }, [logo]);

  // Leaving the phone layout, or navigating, closes the drawer.
  useEffect(() => { if (!phone) setMenuOpen(false); }, [phone]);
  useEffect(() => { setMenuOpen(false); }, [activeKey]);
  // Escape closes it too, from anywhere on the page.
  useEffect(() => {
    if (!(phone && menuOpen)) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [phone, menuOpen]);

  // The drawer is in the DOM only while it is open. It used to stay mounted
  // with the `hidden` attribute, and the sidebar's own `display: flex` beat
  // the attribute — so on every phone-width screen the drawer was painted
  // permanently, fixed over the page, with the close button underneath it.
  // Seen in production on 2026-09-03, the first morning of the new apps.
  const showSidebar = !phone || menuOpen;

  const navigate = (key) => {
    setMenuOpen(false);
    if (onNavigate) onNavigate(key);
  };

  // One element, up to two places in the tree (the phone top bar and the open
  // drawer), so it is built once. The alt text defaults to the wordmark the
  // logo replaced: a picture with no accessible name would take the app's
  // identity away from anyone who cannot see it.
  const wordmark = typeof brand === 'string' ? brand : '';
  const brandMark = (
    <div className="fw-desktop__brand">
      {logo && !logoFailed ? (
        <img
          className="fw-desktop__brand-logo"
          src={logo}
          alt={logoAlt || wordmark}
          onError={() => setLogoFailed(true)}
        />
      ) : brand}
    </div>
  );

  const userBlock = user ? (
    <div className="fw-desktop__user">
      <div className="fw-desktop__user-name">{user.name}</div>
      {user.role ? <div className="fw-desktop__user-role">{user.role}</div> : null}
      {onLogout ? (
        <button type="button" className="fw-desktop__logout" onClick={onLogout}>
          <Icon name="logout" />
          <span>Sign out</span>
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div
      className={['fw-app', 'fw-desktop', phone ? 'fw-desktop--phone' : '', className].filter(Boolean).join(' ')}
      data-layout={phone ? 'phone' : 'desktop'}
      {...rest}
    >
      {phone ? (
        <header className="fw-desktop__topbar">
          <button
            type="button"
            className="fw-desktop__menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen ? 'true' : 'false'}
            aria-controls="fw-desktop-drawer"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <Icon name={menuOpen ? 'x' : 'hamburger'} />
          </button>
          {brandMark}
          {topRight ? <div className="fw-desktop__topright">{topRight}</div> : null}
        </header>
      ) : null}

      {phone && menuOpen ? (
        <div className="fw-desktop__backdrop" onClick={() => setMenuOpen(false)} role="presentation" />
      ) : null}

      {showSidebar ? (
      <aside
        id="fw-desktop-drawer"
        className={['fw-desktop__sidebar', phone ? 'fw-desktop__sidebar--drawer fw-desktop__sidebar--open' : ''].filter(Boolean).join(' ')}
        aria-label="Navigation"
      >
        {phone ? (
          <div className="fw-desktop__drawer-head">
            {brandMark}
            <button type="button" className="fw-desktop__close" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
              <Icon name="x" />
            </button>
          </div>
        ) : brandMark}
        {/* The ONLY scrolling box in the sidebar. The brand used to be inside
            it — measured in Chromium at 1440x900 with an admin's 26
            destinations, the sidebar's scrollHeight was 1464 against a 900
            client height, so reaching Records took the app's name off the top
            of the screen; and as a shrinkable flex item carrying `overflow:
            hidden` the brand's own content box was crushed to 0px, which is
            the wordmark sheared through the letters that the owner
            photographed. A header that must stay put cannot live in the box
            that scrolls. */}
        <div className="fw-desktop__scroll">
          <nav className="fw-desktop__nav">
            <NavGroups groups={groups} activeKey={activeKey} onNavigate={navigate} onToggleGroup={onToggleGroup} />
          </nav>
          {userBlock}
        </div>
      </aside>
      ) : null}

      <div className="fw-desktop__main">
        {!phone && topRight ? <div className="fw-desktop__topright">{topRight}</div> : null}
        <main className="fw-desktop__content">{children}</main>
      </div>
    </div>
  );
}

export default DesktopShell;
